/**
 * rubricaDeTexto — firmar escribiendo el nombre.
 *
 * Es la manera que más se usa cuando se firma desde el ordenador: dibujar con
 * el ratón sale tembloroso y no se parece a la firma de nadie, y escanear la de
 * papel exige tener un escáner a mano. Escribir el nombre en letra manuscrita
 * da algo legible, igual en cada ronda, y que la persona reconoce como suyo.
 *
 * ── Qué vale como firma y qué no ────────────────────────────────────────
 *
 * Lo que da valor a una firma aquí no es el trazo: es que el servidor haya
 * comprobado que quien aprieta el botón entró con la cuenta del firmante. El
 * trazo es la marca visible de ese acto. Por eso un nombre escrito es tan
 * válido como un garabato —y por eso **el texto por defecto es el nombre del
 * firmante, no un campo en blanco**: ponerlo vacío invita a escribir cualquier
 * cosa, y una rúbrica que dice «xx» en el acta no le sirve de nada a quien la
 * lea dentro de dos años.
 *
 * ── Por qué se mide antes de pintar ────────────────────────────────────
 *
 * El hueco de la celda del acta es apaisado y estrecho. «Ana Gil» y «María
 * Guadalupe Fernández de Córdova» no pueden ir del mismo tamaño: con un cuerpo
 * fijo, el nombre largo se sale o se corta. Así que el cuerpo de letra se
 * elige midiendo el nombre de verdad y bajando hasta que entra.
 */

/** La proporción del lienzo, la misma que la del hueco donde acaba. */
export const PROPORCION = 3.2;

/** Lo que se guarda, en píxeles. Igual que en una imagen subida. */
export const ANCHO = 900;

/** Lo más largo que se acepta. Más que esto no es un nombre. */
export const TOPE_LETRAS = 48;

/**
 * Los tipos de letra que se ofrecen.
 *
 * Son pilas de fuentes del sistema, no fuentes descargadas a propósito. Una
 * fuente web tendría que terminar de cargarse ANTES de pintar el lienzo, y si
 * no lo ha hecho el navegador pinta con la de reserva sin avisar: la persona
 * elegiría una letra y le quedaría estampada otra. El respaldo `cursive` no es
 * decorativo: es lo que se usa en un Linux o un móvil que no tenga ninguna de
 * las anteriores.
 */
export const LETRAS = [
  { id: 'inglesa', nombre: 'Inglesa',
    pila: '"Segoe Script", "Brush Script MT", "Bradley Hand", cursive' },
  { id: 'redonda', nombre: 'Redonda',
    pila: '"Lucida Handwriting", "Apple Chancery", "Comic Sans MS", cursive' },
  { id: 'palo', nombre: 'De imprenta',
    pila: 'Georgia, "Times New Roman", serif' }
];

export function pilaDeLetra(id) {
  const l = LETRAS.find(x => x.id === id);
  return (l || LETRAS[0]).pila;
}

/**
 * El nombre, aseado: sin espacios de sobra y sin saltos de línea.
 *
 * Los saltos se quitan en vez de respetarse porque el lienzo es de una sola
 * línea: un «\n» pegado al pegar un nombre desde otro sitio dejaría la mitad
 * del nombre fuera del dibujo, sin que nada lo señalara.
 */
export function nombreLimpio(texto) {
  return String(texto == null ? '' : texto)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, TOPE_LETRAS);
}

/**
 * El cuerpo de letra más grande con el que el nombre todavía entra.
 *
 * Recibe `medir` —una función que, dado un cuerpo, devuelve lo que mide el
 * texto— en vez de usar el lienzo por dentro. Así la decisión se puede probar
 * sin navegador, que es justo la parte que se puede equivocar: pintar, no.
 */
export function cuerpoQueEntra({ medir, anchoDisponible, cuerpoMaximo = 120, cuerpoMinimo = 14 }) {
  if (!(anchoDisponible > 0)) return cuerpoMinimo;
  let cuerpo = cuerpoMaximo;
  // De uno en uno y no por bisección: el recorrido es de cien pasos como mucho,
  // cada medida cuesta microsegundos, y una bisección sobre una función que no
  // es estrictamente monótona —el ajuste de glifos no lo es— puede pasarse de
  // largo. Lo simple aquí también es lo correcto.
  while (cuerpo > cuerpoMinimo && medir(cuerpo) > anchoDisponible) cuerpo -= 1;
  return cuerpo;
}

/**
 * Pinta el nombre y devuelve el PNG en base64 pelado, como el lienzo.
 *
 * Transparente y en negro, por lo mismo que el dibujo a mano: esto acaba en un
 * papel blanco, y el modo oscuro de la aplicación no tiene nada que decir sobre
 * lo que se imprime.
 *
 * @returns {string|null} null si no hay nombre que pintar.
 */
export function rubricaDeTexto({ texto, letra = 'inglesa', ancho = ANCHO }) {
  const nombre = nombreLimpio(texto);
  if (!nombre) return null;

  const alto = Math.round(ancho / PROPORCION);
  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;

  const ctx = lienzo.getContext('2d');
  const pila = pilaDeLetra(letra);
  // Un 12% de margen a cada lado: pegado al borde no parece una firma, y las
  // letras manuscritas tienen rabos que se salen de lo que mide el texto.
  const anchoDisponible = ancho * 0.76;

  const cuerpo = cuerpoQueEntra({
    medir: (c) => {
      ctx.font = `${c}px ${pila}`;
      return ctx.measureText(nombre).width;
    },
    anchoDisponible,
    cuerpoMaximo: Math.round(alto * 0.62)
  });

  ctx.font = `${cuerpo}px ${pila}`;
  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  // Sobre la raya, no centrado en el alto: una firma se apoya en la línea, y
  // dejar aire debajo es lo que la hace parecer escrita y no incrustada.
  ctx.fillText(nombre, ancho / 2, Math.round(alto * 0.72));

  return lienzo.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
}
